/**
 * Absolute top-edge frost strip for cards.
 * Web uses CSS linear-gradient (no expo-linear-gradient view).
 * Native uses a memoized LinearGradient.
 *
 * Drop into an `overflow: 'hidden'` parent — does not wrap children.
 * Pass `theme` from the parent when possible to avoid an extra Redux subscription per list row.
 */
import React, { memo, useMemo } from 'react';
import { Platform, StyleSheet, View, type StyleProp, type ViewStyle } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import {
  FROST_HEIGHT,
  FROST_HEIGHT_COMPACT,
  frostCssLinearGradient,
  getFrostColors,
  type FrostColors,
  type FrostTone,
} from './frostTokens';

export type CardFrostStripProps = {
  /** Preset tone; ignored when `colors` is set. */
  tone?: FrostTone;
  /** Explicit gradient stops (e.g. one-offs). Wins over `tone`. Prefer `tone` for lists. */
  colors?: FrostColors | string[];
  /** Override theme; true = light UI. Prefer passing this in lists. */
  theme?: boolean;
  height?: number;
  compact?: boolean;
  style?: StyleProp<ViewStyle>;
  /** Opt-in frost strip. Default false — pass `enabled` (or `enabled={true}`) to show. */
  enabled?: boolean;
};

const ABSOLUTE: ViewStyle = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  zIndex: 0,
};

const CardFrostStripInner = memo(function CardFrostStripInner({
  tone = 'default',
  colors: colorsProp,
  theme,
  height,
  compact = false,
  style,
  enabled = false,
}: CardFrostStripProps & { theme: boolean }) {
  const resolved = useMemo((): FrostColors | null => {
    if (!enabled) return null;
    if (colorsProp && colorsProp.length >= 2) {
      return [colorsProp[0], colorsProp[1]] as FrostColors;
    }
    return getFrostColors(tone, theme);
  }, [enabled, colorsProp?.[0], colorsProp?.[1], tone, theme]);

  const stripHeight = height ?? (compact ? FROST_HEIGHT_COMPACT : FROST_HEIGHT);

  if (!resolved) return null;

  if (Platform.OS === 'web') {
    return (
      <View
        pointerEvents="none"
        style={[
          ABSOLUTE,
          {
            height: stripHeight,
            backgroundImage: frostCssLinearGradient(resolved),
          } as ViewStyle,
          style,
        ]}
      />
    );
  }

  return (
    <LinearGradient
      pointerEvents="none"
      colors={[resolved[0], resolved[1]]}
      start={{ x: 0, y: 0 }}
      end={{ x: 0, y: 1 }}
      style={[ABSOLUTE, { height: stripHeight }, style]}
    />
  );
});

/** Reads theme from Redux only when parent did not pass `theme`. */
function CardFrostStripWithStore(props: Omit<CardFrostStripProps, 'theme'>) {
  const theme = Boolean(themeSelector());
  return <CardFrostStripInner {...props} theme={theme} />;
}

const CardFrostStrip = memo(function CardFrostStrip(props: CardFrostStripProps) {
  if (typeof props.theme === 'boolean') {
    return <CardFrostStripInner {...props} theme={props.theme} />;
  }
  return <CardFrostStripWithStore {...props} />;
});

export default CardFrostStrip;

/** Stable empty style helper for parents that need overflow clip. */
export const frostParentClipStyle: ViewStyle = StyleSheet.create({
  clip: { overflow: 'hidden', position: 'relative' },
}).clip;
